import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  CircularProgress,
  Avatar,
  Button,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiCategory } from "../../services/AllServices";
import { Form, FormikProvider, useFormik } from "formik";
import { SubcategorySchema } from "../../utils/Schema";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { IMAGE_BASE } from "../../utils/constants";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { RemoveCircle } from "@mui/icons-material";
import { trimFormValues } from "../../utils/helpers";

const PreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 100,
  height: 100,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[3],
  cursor: "pointer",
  "&:hover .remove-btn": {
    opacity: 1,
  },
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  padding: 4,
  opacity: 0.8,
  transition: "opacity 0.3s",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
    opacity: 1,
  },
}));

function AddEditSubCategory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const { id, categoryid } = useParams();
  const [data, setData] = useState();
    const [existingImages, setExistingImages] = useState([]);
  

  // Fetch data for the edit mode
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiCategory.getById({ id: id });
        setData(response?.data?.data);

        // Set existing images as previews (for edit mode)
        if (response?.data?.data?.categoryImage?.length > 0) {
          setExistingImages(response.data.data.categoryImage);
          setImagePreviews(response.data.data.categoryImage.map(img => IMAGE_BASE + img));
        }
      } catch (error) {
        errorHandler(error);
      }
    };
if(id){
  fetchData();

}
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: SubcategorySchema,
    initialValues: {
      id: data?._id || "",
      name: data?.name || "",
      description: data?.description || "",
      image: existingImages.length > 0 ? existingImages : [], 
      status: data?.status || 1,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);
        
        const formData = new FormData();

        if (id) {
          formData.append("parentCategory", data?.parentCategory?._id || data?.parentCategory);
        } else {
          formData.append("parentCategory", categoryid);
        }
        formData.append("id", trimmedValues.id || "");

        formData.append("name", trimmedValues.name);
        formData.append("description", trimmedValues.description);

        // Append images to FormData
        selectedImages.forEach((image) => {
          formData.append("categoryImage", image);
        });

        const response = !id
          ? await apiCategory.add(formData)
          : await apiCategory.edit(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        console.error(error);
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleImageChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    // Allowed file types
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp'];

    // Validate file types
    const invalidFiles = files.filter(file => {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      return !allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid file format. Only PNG, JPG, JPEG, and WEBP formats are allowed.`);
      event.target.value = ''; // Clear the input
      return;
    }

    const newImages = [];
    const newPreviews = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)) {
        newImages.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    }

    if (newImages.length) {
      setImageLoading(true);
      setSelectedImages([...selectedImages, ...newImages]);
      setImagePreviews([...imagePreviews, ...newPreviews]);
      formik.setFieldValue("image", [...formik.values.image, ...newImages]);
      setTimeout(() => {
        setImageLoading(false);
      }, 2000);
    }
  };

  const handleRemoveImage = (index) => {
      if (index < existingImages.length) {
        // Remove from existingImages
        const updatedExisting = [...existingImages];
        updatedExisting.splice(index, 1);
        setExistingImages(updatedExisting);
        // Update previews after removal
        const newPreviews = [
          ...updatedExisting.map((img) => IMAGE_BASE + img),
          ...selectedImages.map((file) => URL.createObjectURL(file)),
        ];
        setImagePreviews(newPreviews);
        // Update formik values
        formik.setFieldValue("image", [...updatedExisting, ...selectedImages]);
      } else {
        // Remove from selectedImages
        const selectedIndex = index - existingImages.length;
        const updatedSelected = [...selectedImages];
        updatedSelected.splice(selectedIndex, 1);
        setSelectedImages(updatedSelected);
        const newPreviews = [
          ...existingImages.map((img) => IMAGE_BASE + img),
          ...updatedSelected.map((file) => URL.createObjectURL(file)),
        ];
        setImagePreviews(newPreviews);
        formik.setFieldValue("image", [...existingImages, ...updatedSelected]);
      }
    };


  return (
    <Box>
      <title>{id ? "Edit" : "Add"} Sub Category</title>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Subcategory`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }} md={12}>
                  <input
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                    multiple
                  />
                  <Box
                    mb={1}
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                  >
                    {imagePreviews.length === 0 && (
                      <Typography variant="subtitle2" color="text.secondary" sx={{ width: "100%" }}>
                        No images uploaded yet
                      </Typography>
                    )}
                    {imagePreviews.map((src, index) => (
                      <PreviewContainer
                        key={index}
                        title="Click to change image"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Avatar
                          src={src}
                          alt={`preview-${index}`}
                          variant="rounded"
                          sx={{ width: "100%", height: "100%" }}
                        />
                        <RemoveButton
                          size="small"
                          aria-label="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(index);
                          }}
                          className="remove-btn"
                        >
                          <RemoveCircle fontSize="small" />
                        </RemoveButton>
                      </PreviewContainer>
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {id ? "Edit Images" : "Upload Images"}
                  </Button>
                  {formik.touched.image && formik.errors.image && (
                    <FormHelperText error>{formik.errors.image}</FormHelperText>
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Subcategory Name"
                    value={formik.values.name}
                    onChange={(e) => formik.setFieldValue("name", e.target.value)}
                    onBlur={(e) => formik.setFieldValue("name", e.target.value.trim())}
                    name="name"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 50 }}
                    placeholder="e.g., Electric, Gas, Diesel, Hybrid"
                  />
                </Grid>

                {/* <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={formik.values.status}
                      onChange={(e) => formik.setFieldValue("status", e.target.value)}
                      label="Status"
                      error={Boolean(formik.touched.status && formik.errors.status)}
                    >
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                    </Select>
                    {formik.touched.status && formik.errors.status && (
                      <FormHelperText error>{formik.errors.status}</FormHelperText>
                    )}
                  </FormControl>
                </Grid> */}

                <Grid size={{ xs: 12 }} md={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    name="description"
                    error={Boolean(formik.touched.description && formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    inputProps={{ maxLength: 200 }}
                    placeholder="Brief description of the subcategory for SEO and clarity"
                  />
                </Grid>

                <Grid size={{ xs: 12 }} md={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Subcategory
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditSubCategory;
